<script lang="ts" setup>
import { computed } from 'vue';
import { IconSelector } from '@/icons';
import { ObFlexGrid, ObFlexGridItem } from '../flex-grid';
import { ObPagination } from '../pagination';
import { ObSpace } from '../space';
import { UiSelectCompact } from '../ui-select-compact';
const props = withDefaults(
  defineProps<{
    page: number;
    pageSize: number;
    pageSizeOptions: number[];
    total: number;
    label?: string;
  }>(),
  {
    label: 'Rows',
  },
);
const emit = defineEmits<{
  'update:page': [value: number];
  'update:pageSize': [value: number];
}>();
const displayedLabel = computed(() => {
  const start = (props.page - 1) * props.pageSize + 1;
  const end = Math.min(props.page * props.pageSize, props.total);
  return `${start}-${end} of ${props.total}`;
});
const selectOptions = computed(() =>
  props.pageSizeOptions.map((n) => ({ label: String(n), value: String(n) })),
);
function onPageSizeChange(value: string | null) {
  emit('update:pageSize', Number(value ?? props.pageSize));
  emit('update:page', 1);
}
</script>

<template>
  <div :class="$style.root">
    <ObFlexGrid justify-content="between" align-items="center">
      <ObFlexGridItem>
        <div :class="$style.pagination">
          <ObSpace spacing="4" align-y="center" size="auto">
            <div>
              <UiSelectCompact
                :model-value="String(props.pageSize)"
                :options="selectOptions"
                :chevron="false"
                variant="secondary"
                @update:model-value="onPageSizeChange"
              >
                <template #icon>
                  <IconSelector :size="18" />
                </template>
              </UiSelectCompact>
            </div>
          </ObSpace>
        </div>
      </ObFlexGridItem>
      <ObFlexGridItem size="auto">
        <ObSpace spacing="4" align-y="center">
          <div :class="$style.displayedLabel">{{ displayedLabel }}</div>
          <ObPagination
            :model-value="props.page"
            :page-size="props.pageSize"
            :total="props.total"
            @update:model-value="emit('update:page', $event)"
          />
        </ObSpace>
      </ObFlexGridItem>
    </ObFlexGrid>
  </div>
</template>

<style lang="scss" module>
@use '../../styles/typography';

.root {
  position: sticky;
  bottom: 0;
  left: 0;
  padding: 8px;
  background-color: #fff;
  border-top: 1px solid var(--ui2-stroke-tertiary);
  margin-top: -1px;
  @include typography.style('data-s-medium');
}

.pagination {
  padding-left: 0;
}

.displayedLabel {
  @include typography.style('data-s-medium');
  color: var(--ui2-content-secondary, #655e60);
}
</style>
